/**
 * Shieldz Merchant Dashboard — TanStack Router setup.
 *
 * Code-based routing for explicit TypeScript safety without a build-time
 * route generator. Routes:
 *
 *   /                   → Home (revenue hero, recent links)
 *   /setup              → Onboarding wizard (when merchant !exists)
 *   /activity           → Activity list (paginated)
 *   /activity/$id       → Single payment-link detail
 *   /products           → Products and prices (the catalog)
 *   /links              → Payment links + buy button code
 *   /settings           → Settings (brand, settlement, notifications, dev mode)
 *   /webhooks           → Webhook config + delivery log (developer mode)
 *   /api-keys           → API keys list + mint (developer mode)
 *
 * Auth: every page authenticates via Telegram-Init-Data on each API call.
 * If `getMyMerchant()` returns exists=false, the root layout redirects to
 * /setup. Once the merchant is set up, the sidebar shell wraps the
 * outlet.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { RootLayout } from "./routes/__root";
import { AuthGate } from "./components/AuthGate";
import { SignInScreen } from "./routes/login";
import { Home } from "./routes/home";
import { QuickStart } from "./routes/quickstart";
import { SetupScreen } from "./routes/setup";
import { Activity } from "./routes/activity";
import { ActivityDetail } from "./routes/activity.$id";
import { Settings } from "./routes/settings";
import { Webhooks } from "./routes/webhooks";
import { ApiKeysScreen } from "./routes/api-keys";
import { Billing } from "./routes/billing";
import { Status } from "./routes/status";
import { Coins } from "./routes/coins";
import { ClaimScreen } from "./routes/claim";
import { Stats } from "./routes/stats";
import { Pages } from "./routes/pages";
import { Unlocks } from "./routes/unlocks";
import { Products } from "./routes/products";
import { PaymentLinks } from "./routes/links";
import { Ads } from "./routes/ads";
import { getMyMerchant } from "./lib/api";
import { isDeveloperMode } from "./lib/developer-mode";
import { applyTitle } from "./lib/page-title";

const rootRoute = createRootRoute({
  component: () => (
    <AuthGate>
      <RootLayout />
    </AuthGate>
  ),
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  component: SetupScreen,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search): { redirect?: string } => ({
    redirect:
      typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: () => <SignInScreen mode="login" />,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  validateSearch: (search): { redirect?: string } => ({
    redirect:
      typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: () => <SignInScreen mode="signup" />,
});

const claimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/claim",
  validateSearch: (search): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: ClaimScreen,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    const m = await getMyMerchant();
    if (!m.exists) throw redirect({ to: "/setup" });
    return { merchant: m };
  },
  component: Home,
});

const quickstartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quickstart",
  component: QuickStart,
});

const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activity",
  component: Activity,
});

const activityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activity/$id",
  component: ActivityDetail,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: Settings,
});

const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/status",
  component: Status,
});

const coinsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coins",
  component: Coins,
});

// Developer-mode routes — only reachable when the localStorage flag is on.
// We don't strictly hide them at the router level (the URL can still be
// typed), but the sidebar nav only links to them in developer mode, and
// the screen components render a hint if the flag is off.
const webhooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/webhooks",
  beforeLoad: () => {
    if (!isDeveloperMode()) throw redirect({ to: "/settings" });
  },
  component: Webhooks,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/api-keys",
  beforeLoad: () => {
    if (!isDeveloperMode()) throw redirect({ to: "/settings" });
  },
  component: ApiKeysScreen,
});

// V1.4 — Billing (Free / Pro $99) and public pages (tip/donate/fund)
// management. Both are first-class merchant features, not gated behind
// developer-mode.
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  component: Billing,
});

const pagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pages",
  component: Pages,
});

// Catalog: products + prices, and payment links / buy buttons (Stripe's model).
const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  component: Products,
});

const linksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/links",
  component: PaymentLinks,
});

const unlocksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/unlocks",
  component: Unlocks,
});

// Platform-only — the page + nav are gated to the Shieldz account
// (is_platform_admin); the backend hard-gates every /api/admin/ads* call too.
const adsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ads",
  component: Ads,
});

// Platform-only stats. Backend hard-gates /api/admin/platform-stats with 403;
// the nav link is shown only to the Shieldz account.
const statsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stats",
  component: Stats,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  quickstartRoute,
  setupRoute,
  loginRoute,
  signupRoute,
  claimRoute,
  activityRoute,
  activityDetailRoute,
  settingsRoute,
  statusRoute,
  coinsRoute,
  billingRoute,
  pagesRoute,
  productsRoute,
  linksRoute,
  unlocksRoute,
  adsRoute,
  statsRoute,
  webhooksRoute,
  apiKeysRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

// GA's history listener records the page view as soon as the URL changes, so
// the title has to be set before the push, not after the route renders. A
// blocker runs right before every push/replace and never blocks.
router.history.block({
  blockerFn: ({ nextLocation }) => {
    applyTitle(nextLocation.pathname);
    return false;
  },
  enableBeforeUnload: false,
});
// Back/forward (popstate) skips push blockers.
router.subscribe("onResolved", ({ toLocation }) => applyTitle(toLocation.pathname));

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
